import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import '../../pdfWorkerConfig';
import '../../DocumentWidget.css';
import { StorageService } from '../../../../sdk/storageService';

const Clean = ({ documentUrl, documentType, pageNumber = 1, autoScroll = false, scrollInterval = 10 }) => {
  if (!documentUrl) {
    return <div className="document-widget__placeholder">אין מסמך</div>;
  }

  if (documentType === 'pdf') {
    return (
      <PdfViewer
        documentUrl={documentUrl}
        pageNumber={pageNumber}
        autoScroll={autoScroll}
        scrollInterval={scrollInterval}
      />
    );
  }

  if (documentType === 'docx') {
    return (
      <DocxViewer
        documentUrl={documentUrl}
        pageNumber={pageNumber}
        autoScroll={autoScroll}
        scrollInterval={scrollInterval}
      />
    );
  }

  return <div className="document-widget__error">סוג מסמך לא נתמך</div>;
};

// ─── PDF Viewer ──────────────────────────────────────────────────────
const PdfViewer = ({ documentUrl, pageNumber, autoScroll, scrollInterval }) => {
  const [pdfData, setPdfData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(pageNumber);
  const [containerWidth, setContainerWidth] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const loadPdf = async () => {
      try {
        setPdfData(null);
        setLoadError('');
        const bytes = await StorageService.downloadDocumentBytes(documentUrl);
        if (!cancelled) setPdfData({ data: new Uint8Array(bytes) });
      } catch (err) {
        console.error('PDF download error:', err);
        if (!cancelled) setLoadError(`שגיאה בטעינת המסמך: ${err.message}`);
      }
    };
    loadPdf();
    return () => { cancelled = true; };
  }, [documentUrl]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!autoScroll || !numPages || numPages <= 1) return;
    const interval = setInterval(() => {
      setCurrentPage((prev) => (prev >= numPages ? 1 : prev + 1));
    }, scrollInterval * 1000);
    return () => clearInterval(interval);
  }, [autoScroll, scrollInterval, numPages]);

  useEffect(() => {
    if (!autoScroll) setCurrentPage(pageNumber);
  }, [pageNumber, autoScroll]);

  const onDocumentLoadSuccess = useCallback(({ numPages: total }) => {
    setNumPages(total);
  }, []);

  if (loadError) return <div className="document-widget__error">{loadError}</div>;

  return (
    <div className="document-widget__pdf-container" ref={containerRef}>
      {pdfData ? (
        <Document
          file={pdfData}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={(err) => console.error('PDF load error:', err)}
          loading={<LoadingSpinner />}
          error={<div className="document-widget__error">שגיאה בטעינת המסמך</div>}
        >
          {containerWidth && (
            <Page
              pageNumber={currentPage}
              width={containerWidth}
              loading={<LoadingSpinner />}
            />
          )}
        </Document>
      ) : (
        <LoadingSpinner />
      )}
    </div>
  );
};

// ─── DOCX Viewer (Google Docs Viewer) ────────────────────────────────
const DocxViewer = ({ documentUrl }) => {
  const publicUrl = documentUrl.replace(/&token=[^&]+/, '');
  const googleSrc = `https://docs.google.com/gview?url=${encodeURIComponent(publicUrl)}&embedded=true`;

  return (
    <div className="document-widget__docx-display">
      <iframe
        src={googleSrc}
        title="Document Viewer"
      />
      <img
        src="/inorai-logo.png"
        alt=""
        className="document-widget__docx-corner-logo"
      />
    </div>
  );
};

// ─── Shared ──────────────────────────────────────────────────────────
const LoadingSpinner = () => (
  <div className="document-widget__loading">
    <div className="document-widget__spinner" />
    טוען מסמך...
  </div>
);

export default Clean;
